import React, { useEffect, useRef } from 'react';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../util/Firebase';
import videojs from 'video.js';
import '../css/VideoPlayer.css';
import 'video.js/dist/video-js.css';
import Poster from '../images/VideoPreview.webp'

interface VideoPlayerProps {
    fileUrl: string;
    isPremium: boolean;
    isGenericPoster?: boolean;
    isAuthenticated?: boolean;
    onVideoComplete?: () => void;
    isCompletingLesson?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
    fileUrl, 
    isPremium, 
    isGenericPoster = false, 
    isAuthenticated = false,
    onVideoComplete,
    isCompletingLesson = false
}) => {
    const videoRef = useRef<any | null>(null);
    const playerRef = useRef<any | null>(null);
    const hasTriggeredCompletion = useRef(false);

    useEffect(() => {
        if (videoRef.current && !playerRef.current) {
            const storageRef = ref(storage, fileUrl);
            getDownloadURL(storageRef)
                .then((url: string) => {
                    const options: any = {
                        controls: true,
                        autoplay: false,
                        preload: 'auto',
                        sources: [{
                            src: url,
                            type: 'video/mp4'
                        }]
                    };

                    if (isGenericPoster) {
                        options.poster = Poster;
                    }

                    playerRef.current = videojs(videoRef.current, options);

                    // Add completion tracking for authenticated users
                    if (isAuthenticated && onVideoComplete) {
                        playerRef.current.on('timeupdate', () => {
                            const player = playerRef.current;
                            if (player && !hasTriggeredCompletion.current) {
                                const currentTime = player.currentTime();
                                const duration = player.duration();
                                
                                // Trigger completion when video reaches 90% or near the end
                                if (duration > 0 && currentTime >= duration * 0.9) {
                                    hasTriggeredCompletion.current = true;
                                    onVideoComplete();
                                }
                            }
                        });

                        // Also trigger on 'ended' event as a fallback
                        playerRef.current.on('ended', () => {
                            if (!hasTriggeredCompletion.current) {
                                hasTriggeredCompletion.current = true;
                                onVideoComplete();
                            }
                        });
                    }
                })
                .catch((error: Error) => {
                    console.error("Error fetching video URL:", error);
                });
        }

        return () => {
            if (playerRef.current) {
                playerRef.current.dispose();
                playerRef.current = null;
            }
        };
    }, [fileUrl, isAuthenticated]); // Removed onVideoComplete from dependencies

    // Reset completion trigger when video changes
    useEffect(() => {
        hasTriggeredCompletion.current = false;
    }, [fileUrl]);

    if (!isPremium) {
        return null;
    }

    return (
        <div className="video-container" style={{ position: 'relative' }}>
            <video ref={videoRef} className="video-js vjs-default-skin video-player" />
            {isCompletingLesson && (
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '0.5em 1em',
                    borderRadius: '0.25em',
                    fontSize: '0.9em',
                    zIndex: 1000
                }}>
                    Saving progress...
                </div>
            )}
        </div>
    );
};

export default VideoPlayer;